import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import MobileHeader from '../components/MobileHeader';

const ChangePasswordScreen = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const { userToken } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/api/Auth/change-password', {
        currentPassword,
        newPassword,
      });
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const eyeButton = (show, toggle) => (
    <InputAdornment position="end">
      <IconButton onClick={toggle} edge="end" size="small">
        {show ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader title="Change Password" showBack={true} />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 2, pb: 4 }}>
        {/* Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 1 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: '#E3F2FD',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: 36, color: '#1976D2' }} />
          </Box>
        </Box>

        <Typography sx={{ textAlign: 'center', fontSize: 14, color: '#666', mb: 3, px: 2 }}>
          Enter your current password and choose a new one.
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2, borderRadius: 2 }}>
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#555', mb: 0.5 }}>
            Current Password
          </Typography>
          <TextField
            fullWidth
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            size="small"
            sx={{ mb: 2, bgcolor: '#fff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ endAdornment: eyeButton(showCurrent, () => setShowCurrent((v) => !v)) }}
          />

          <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#555', mb: 0.5 }}>
            New Password
          </Typography>
          <TextField
            fullWidth
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            size="small"
            sx={{ mb: 2, bgcolor: '#fff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ endAdornment: eyeButton(showNew, () => setShowNew((v) => !v)) }}
          />

          <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#555', mb: 0.5 }}>
            Confirm New Password
          </Typography>
          <TextField
            fullWidth
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            size="small"
            sx={{ mb: 3, bgcolor: '#fff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ endAdornment: eyeButton(showConfirm, () => setShowConfirm((v) => !v)) }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              height: 50,
              borderRadius: 25,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: 15,
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
            }}
          >
            {submitting ? 'Updating...' : 'Change Password'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePasswordScreen;
